import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { MaterialModule } from 'src/app/material.module';
import { BaseRespone } from 'src/app/models/common.response';
import { extractMaskedMobile } from '../abha-otp-message.util';
import { AbhaOtpResendComponent } from '../abha-otp-resend/abha-otp-resend.component';
import { AbhaService } from '../abha.service';
import { AbhaAccount, AbhaEnrolmentResult, AbhaFindBy, AbhaFindResult, AbhaOtpResult } from '../iabha-info';

const MOBILE_PATTERN = /^[6-9][0-9]{9}$/;
const AADHAAR_PATTERN = /^[0-9]{12}$/;
const OTP_PATTERN = /^[0-9]{6}$/;

// Finds an existing ABHA by mobile number or by Aadhaar number (NHA M1 VRFY_ABHA_301 to 305
// and 401 to 405): search, OTP, pick the account. When ABDM has no ABHA it offers creation.
@Component({
  selector: 'app-abha-find',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, TranslateModule, AbhaOtpResendComponent],
  templateUrl: './abha-find.component.html',
  styleUrl: './abha-find.component.scss',
})
export class AbhaFindComponent implements OnInit {
  @Input() patientId?: number;
  @Input() mobile?: string;
  @Output() readonly found = new EventEmitter<AbhaEnrolmentResult>();
  @Output() readonly createRequested = new EventEmitter<string | undefined>();
  @Output() readonly stateChanged = new EventEmitter<void>();

  findForm: FormGroup;
  txnId = '';
  otpNotice = '';
  notFoundMessage = '';
  accounts: readonly AbhaAccount[] = [];
  isBusy = false;
  private selectionToken = '';
  private selectionTxnId = '';
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private abhaService: AbhaService,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {
    this.findForm = this.fb.group({
      findBy: ['mobile' as AbhaFindBy, Validators.required],
      value: ['', [Validators.required, Validators.pattern(MOBILE_PATTERN)]],
      otp: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(OTP_PATTERN)]],
    });
  }

  get findBy(): AbhaFindBy {
    return this.findForm.get('findBy')?.value;
  }

  get isOtpSent(): boolean {
    return this.txnId.length > 0;
  }

  get hasAccounts(): boolean {
    return this.accounts.length > 0;
  }

  ngOnInit(): void {
    this.findForm.patchValue({ value: this.mobile || '' });
  }

  onFindByChange(): void {
    const pattern = this.findBy === 'aadhaar' ? AADHAAR_PATTERN : MOBILE_PATTERN;
    const value = this.findForm.get('value');
    value?.setValidators([Validators.required, Validators.pattern(pattern)]);
    value?.setValue(this.findBy === 'mobile' ? this.mobile || '' : '');
    this.notFoundMessage = '';
  }

  onSendOtp(): void {
    const value = this.findForm.get('value');
    if (!value || value.invalid) {
      this.findForm.markAllAsTouched();
      return;
    }

    this.isBusy = true;
    this.stateChanged.emit();
    this.abhaService
      .requestLoginOtp({ loginType: this.findBy, loginId: value.value, otpSystem: this.findBy === 'aadhaar' ? 'aadhaar' : 'abdm' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => this.handleOtpResponse(response));
  }

  onVerifyOtp(): void {
    if (this.findForm.invalid) {
      this.findForm.markAllAsTouched();
      return;
    }

    this.isBusy = true;
    this.stateChanged.emit();
    this.abhaService
      .verifyFindOtp({ loginType: this.findBy, txnId: this.txnId, otp: this.findForm.get('otp')?.value })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => this.handleFindResponse(response));
  }

  onSelect(account: AbhaAccount): void {
    if (this.isBusy || account.linkedPatientNumber) {
      return;
    }

    this.isBusy = true;
    this.stateChanged.emit();
    this.abhaService
      .selectFoundAccount({
        selectionToken: this.selectionToken,
        txnId: this.selectionTxnId,
        abhaNumber: account.abhaNumber,
        patientId: this.patientId
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => this.handleSelectResponse(response));
  }

  onCreate(): void {
    this.createRequested.emit(this.findBy === 'aadhaar' ? this.findForm.get('value')?.value : undefined);
  }

  onSearchAgain(): void {
    this.txnId = '';
    this.otpNotice = '';
    this.notFoundMessage = '';
    this.accounts = [];
    this.findForm.get('otp')?.reset({ value: '', disabled: true });
    this.stateChanged.emit();
  }

  photoSource(account: AbhaAccount): string {
    return `data:image/jpeg;base64,${account.profilePhoto}`;
  }

  private handleOtpResponse(response: BaseRespone<AbhaOtpResult>): void {
    this.isBusy = false;
    if (response && response.isSuccess && response.result?.abhaNotFound) {
      this.notFoundMessage = response.result.message;
      this.stateChanged.emit();
      return;
    }

    if (response && response.isSuccess && response.result?.txnId) {
      this.txnId = response.result.txnId;
      this.otpNotice = this.buildOtpNotice(response.result.message);
      this.notFoundMessage = '';
      this.findForm.get('otp')?.enable();
      this.stateChanged.emit();
      return;
    }

    this.stateChanged.emit();
    this.toastr.error(this.resolveError(response, 'ABHA.OTP_FAILED'));
  }

  // Wording from NHA M1 VRFY_ABHA_303 and 403, with the masked number ABDM returned.
  private buildOtpNotice(abdmMessage: string): string {
    const maskedMobile = extractMaskedMobile(abdmMessage);
    if (!maskedMobile) {
      return abdmMessage || '';
    }

    const key = this.findBy === 'aadhaar' ? 'ABHA.OTP_NOTICE_FIND_AADHAAR' : 'ABHA.OTP_NOTICE_FIND_MOBILE';
    return this.translate.instant(key, { mobile: maskedMobile });
  }

  private handleFindResponse(response: BaseRespone<AbhaFindResult>): void {
    this.isBusy = false;
    if (!response || !response.isSuccess || !response.result) {
      this.stateChanged.emit();
      this.toastr.error(this.resolveError(response, 'ABHA.FIND_FAILED'));
      return;
    }

    if (response.result.abhaNotFound) {
      this.onSearchAgain();
      this.notFoundMessage = response.result.message || '';
      return;
    }

    this.showAccounts(response.result);
  }

  // One Aadhaar has one ABHA, so there is nothing to choose; a mobile can have several.
  private showAccounts(result: AbhaFindResult): void {
    this.selectionToken = result.selectionToken || '';
    this.selectionTxnId = result.txnId || '';
    this.accounts = result.accounts;
    this.stateChanged.emit();
    if (this.findBy === 'aadhaar' && result.accounts.length === 1 && !result.accounts[0].linkedPatientNumber) {
      this.onSelect(result.accounts[0]);
    }
  }

  private handleSelectResponse(response: BaseRespone<AbhaEnrolmentResult>): void {
    this.isBusy = false;
    this.stateChanged.emit();
    if (response && response.isSuccess && response.result?.abhaNumber) {
      this.found.emit(response.result);
      return;
    }

    this.toastr.error(this.resolveError(response, 'ABHA.FIND_FAILED'));
  }

  private resolveError(response: BaseRespone<unknown> | null, fallbackKey: string): string {
    if (response && response.errors && response.errors.length > 0) {
      return response.errors.join('\n');
    }

    if (response && response.message) {
      return response.message;
    }

    return this.translate.instant(fallbackKey);
  }
}
